import { FilterOption } from '../components/filter/Filter'

export interface IFlattenedFilterOption<V = string> {
  option: FilterOption<V>
  depth: number
  parentValues: V[]
  isParent: boolean
  isToggle: boolean
}

export const isFilterOptionSelectable = <V>(
  option: FilterOption<V>
): boolean => {
  if (option.disabled) return false
  if (option.selectable === false) return false
  return true
}

export const flattenFilterOptions = <V>(
  options: Array<FilterOption<V>>,
  depth = 0,
  parentValues: V[] = []
): Array<IFlattenedFilterOption<V>> => {
  const result: Array<IFlattenedFilterOption<V>> = []
  options.forEach((option) => {
    const isParent = !!option.children && option.children.length > 0
    result.push({
      option,
      depth,
      parentValues,
      isParent,
      isToggle: option.kind === 'toggle',
    })
    if (isParent) {
      result.push(
        ...flattenFilterOptions(option.children!, depth + 1, [
          ...parentValues,
          option.value,
        ])
      )
    }
  })
  return result
}

export const collectLeafFilterValues = <V>(option: FilterOption<V>): V[] => {
  if (!option.children || option.children.length === 0) {
    return isFilterOptionSelectable(option) ? [option.value] : []
  }
  const childLeaves: V[] = []
  option.children.forEach((child) => {
    childLeaves.push(...collectLeafFilterValues(child))
  })
  return childLeaves
}

export const collectSelectableFilterValues = <V>(
  option: FilterOption<V>
): V[] => {
  const values: V[] = []
  if (isFilterOptionSelectable(option) && option.selectable !== false) {
    if (!option.children || option.children.length === 0) {
      values.push(option.value)
    } else if (option.selectable === true) {
      values.push(option.value)
    }
  }
  if (option.children) {
    option.children.forEach((child) => {
      values.push(...collectSelectableFilterValues(child))
    })
  }
  return values
}

export const findFilterOption = <V>(
  options: Array<FilterOption<V>>,
  value: V
): FilterOption<V> | undefined => {
  for (const option of options) {
    if (option.value === value) return option
    if (option.children) {
      const found = findFilterOption(option.children, value)
      if (found) return found
    }
  }
  return undefined
}
